import { useEffect, useRef, useState, useMemo, useCallback } from 'react';

/**
 * IntersectionObserver 管理器
 * 根据配置选项创建和管理共享的 IntersectionObserver 实例
 */
class IntersectionObserverManager {
    constructor() {
        // 存储不同配置的 observer 信息
        // key: 配置的字符串表示
        // value: { observer, callbacks: Map<element, callback>, elementCount: number }
        this.observerMap = new Map();
    }

    /**
     * 获取配置的唯一标识
     */
    getOptionsKey(options) {
        try {
            // 使用 JSON.stringify 创建唯一 key（适用于基本配置项）
            return JSON.stringify({
                threshold: options.threshold ?? 0,
                rootMargin: options.rootMargin ?? '0px',
                root: options.root ?? null,
            });
        } catch {
            // 如果序列化失败，使用对象引用作为 fallback
            return String(options);
        }
    }

    /**
     * 获取或创建 IntersectionObserver 实例
     */
    getObserverInfo(options) {
        const key = this.getOptionsKey(options);

        if (!this.observerMap.has(key)) {
            const callbacks = new Map();
            const observer = new IntersectionObserver(entries => {
                // 批量处理所有变化的元素
                entries.forEach(entry => {
                    const callback = callbacks.get(entry.target);
                    if (callback) {
                        callback(entry.isIntersecting);
                    }
                });
            }, options);

            this.observerMap.set(key, {
                observer,
                callbacks,
                elementCount: 0,
            });
        }

        return this.observerMap.get(key);
    }

    /**
     * 观察元素
     */
    observe(element, options, callback) {
        const info = this.getObserverInfo(options);
        info.callbacks.set(element, callback);
        info.elementCount++;
        info.observer.observe(element);
    }

    /**
     * 取消观察元素
     */
    unobserve(element, options) {
        const key = this.getOptionsKey(options);
        const info = this.observerMap.get(key);

        if (info && element) {
            try {
                info.observer.unobserve(element);
            } catch (error) {
                // 元素可能已经被移除，忽略错误
            }

            info.callbacks.delete(element);
            info.elementCount--;

            // 如果没有其他元素使用，清理 observer
            if (info.elementCount <= 0) {
                info.observer.disconnect();

                info.observer = null;
                this.observerMap.delete(key);
            }
        }
    }
}

// 创建全局单例管理器
const observerManager = new IntersectionObserverManager();

/**
 * 自定义 Hook：检测元素是否在可视区域内
 * 多个组件共享同一个 IntersectionObserver 实例（相同配置下）
 * @param {Object} options - IntersectionObserver 配置选项
 * @param {number|Array<number>} options.threshold - 元素可见比例阈值，默认 0
 * @param {string} options.rootMargin - 根边距，默认 '0px'
 * @param {Element} options.root - 根元素，默认 null（使用视口）
 * @returns {[React.RefObject, boolean]} 返回 [元素引用, 是否在可视区域内]
 */
const useIsInViewport = (options = {}) => {
    const elementRef = useRef(null);
    const [isInViewport, setIsInViewport] = useState(false);

    // 稳定化 options 对象，合并默认配置
    const stableOptions = useMemo(() => {
        return {
            threshold: 0, // 元素可见比例，0表示只要有一点可见就触发
            rootMargin: '0px', // 根边距
            ...options,
        };
    }, [options]);

    // 创建稳定的回调函数
    const handleIntersection = useCallback(isIntersecting => {
        setIsInViewport(isIntersecting);
    }, []);

    useEffect(() => {
        const element = elementRef.current;
        if (!element) return;

        // 使用共享的 observer 管理器
        observerManager.observe(element, stableOptions, handleIntersection);

        return () => {
            // 清理时取消观察
            observerManager.unobserve(element, stableOptions);
        };
    }, [stableOptions, handleIntersection]);

    return [elementRef, isInViewport];
};

export default useIsInViewport;
// 使用示例
/* 
const MyComponent = () => {
    const [ref, isVisible] = useIsInViewport();

    return (
        <div ref={ref}>
            {isVisible ? '元素在可视范围内' : '元素不在可视范围内'}
        </div>
    );
}; 
*/
